import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
} from "@mui/material";

import api from "../../api/api";
import * as XLSX from "xlsx";

export default function EffortSummary() {
  const [data, setData] = useState([]);

  const [filters, setFilters] = useState({
    start: "",
    end: "",
    client: "",
    project: "",
    user: "",
  });

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);

  const handleChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  /* ===============================
     LOAD FILTER DATA
  =============================== */

  const loadFilters = async () => {
    try {
      const p = await api.get("/projects");
      const u = await api.get("/users");
      const c = await api.get("/billing/clients");

      setProjects(p.data);
      setUsers(u.data);
      setClients(c.data);
    } catch (err) {
      console.error("Filter load error", err);
    }
  };

  /* ===============================
     FETCH SUMMARY DATA
  =============================== */

  const fetchData = async () => {
    try {
      const res = await api.get("/reports/project-effort", {
        params: filters,
      });

      setData(res.data);
    } catch (err) {
      console.error("Effort fetch error", err);
    }
  };

  useEffect(() => {
    loadFilters();
  }, []);

  /* ===============================
     DATE FORMAT
  =============================== */

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    const months = [
      "Jan","Feb","Mar","Apr","May","Jun",
      "Jul","Aug","Sep","Oct","Nov","Dec"
    ];

    const day = String(date.getDate()).padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  /* ===============================
     TOTAL CALCULATION (CORRECT)
  =============================== */

  const totalMinutes = data.reduce(
    (sum, row) => sum + (row.minutes || 0),
    0
  );

  const totalHours = (totalMinutes / 60).toFixed(2);

  /* ===============================
     EXPORT TO EXCEL (FIXED)
  =============================== */

  const exportToExcel = () => {
    const exportData = data.map((row) => ({
      Project: row.project,
      Date: formatDate(row.date),
      User: row.user,
      Minutes: row.minutes,
      Hours: row.minutes / 60, // ❗ no rounding here
    }));

    // ✅ ADD TOTAL ROW
    exportData.push({
      Project: "TOTAL",
      Date: "",
      User: "",
      Minutes: totalMinutes,
      Hours: totalMinutes / 60,
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Optional: column width
    worksheet["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 20 },
      { wch: 10 },
      { wch: 10 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Effort Summary");

    XLSX.writeFile(workbook, "effort_summary.xlsx");
  };

  return (
    <Box p={4}>
      <Typography variant="h5" mb={3}>
        Effort Summary
      </Typography>

      {/* EXPORT BUTTON */}
      <Button
        variant="contained"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
      >
        Export Excel
      </Button>

      {/* ===============================
          FILTER SECTION
      =============================== */}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>

            <Grid item xs={12} md={2}>
              <TextField
                label="Start Date"
                type="date"
                name="start"
                fullWidth
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                label="End Date"
                type="date"
                name="end"
                fullWidth
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                select
                label="Client"
                name="client"
                fullWidth
                value={filters.client}
                onChange={handleChange}
              >
                <MenuItem value="">All</MenuItem>
                {clients.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                select
                label="Project"
                name="project"
                fullWidth
                value={filters.project}
                onChange={handleChange}
              >
                <MenuItem value="">All</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                select
                label="User"
                name="user"
                fullWidth
                value={filters.user}
                onChange={handleChange}
              >
                <MenuItem value="">All</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={fetchData}
                sx={{ height: "56px" }}
                fullWidth
              >
                Fetch
              </Button>
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* ===============================
          DATA TABLE
      =============================== */}

      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Minutes</TableCell>
                <TableCell>Hours</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.project}</TableCell>
                  <TableCell>{formatDate(row.date)}</TableCell>
                  <TableCell>{row.user}</TableCell>
                  <TableCell>{row.minutes}</TableCell>
                  <TableCell>
                    {(row.minutes / 60).toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}

              {/* ✅ TOTAL ROW */}
              <TableRow>
                <TableCell><b>TOTAL</b></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell><b>{totalMinutes}</b></TableCell>
                <TableCell><b>{totalHours}</b></TableCell>
              </TableRow>

            </TableBody>

          </Table>
        </CardContent>
      </Card>

    </Box>
  );
}